import { EXPLICIT_GRANT_ONLY_PERMISSIONS, Permission, PERMISSIONS } from '@/constants/permissions';

/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(initialState: { currentUser?: API.CurrentUser } | undefined) {
  const { currentUser } = initialState ?? {};
  const userPermissions = currentUser?.permissions || [];
  const isSuperAdmin = currentUser?.is_super_admin || false;
  const isCurrentUserCsr = (currentUser as any)?.tenant_user_roles?.some((r: any) => r.role === 'CSR') ?? false;

  // Helper function to check if user has a specific permission
  const hasPermission = (permission: string | Permission): boolean => {
    // Super admin has all permissions, except explicit-grant-only ones
    if (isSuperAdmin && !EXPLICIT_GRANT_ONLY_PERMISSIONS.includes(permission)) return true;
    return userPermissions.includes(permission);
  };

  // Helper function to check if user has any of the specified permissions
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(hasPermission);
  };

  // Helper function to check if user has all of the specified permissions
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(hasPermission);
  };

  const perm = {
    // Basic user authentication 
    user: !!currentUser?.id,

    // Super admin status
    isSuperAdmin,

    // Whether the current user holds the CSR role for this tenant
    isCurrentUserCsr,

    // Permission-based access control
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Specific permission checks for common features (super admin automatically has all)
    canManageTenantRoles: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_TENANT_ROLES),
    canManageEmployeeRoles: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_EMPLOYEE_ROLES),
    canManageUsers: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_USERS),
    // Employee private information (DOB, driver's license, fuel card, pay) on the Users page
    canViewUserPrivateInfo: isSuperAdmin || hasPermission(PERMISSIONS.USER_PRIVATE_INFO_VIEW),
    canUserSmsPreferencesUpdate: isSuperAdmin || hasPermission(PERMISSIONS.USER_SMS_PREFERENCES_UPDATE),
    // Explicit grant only — hasPermission() already skips the super-admin shortcut for it
    canLoginAsUser: hasPermission(PERMISSIONS.USER_LOGIN_AS),
    canFieldManagerCrud: isSuperAdmin || hasPermission(PERMISSIONS.FIELD_MANAGER_CRUD),
    canCsrManagerCrud: isSuperAdmin || hasPermission(PERMISSIONS.CSR_MANAGER_CRUD),
    canViewCsrPerformance: isSuperAdmin || hasPermission(PERMISSIONS.CSR_PERFORMANCE_VIEW),
    canViewCsrProductivity: isSuperAdmin || hasPermission(PERMISSIONS.CSR_PRODUCTIVITY_VIEW),
    // Explicit-grant-only: this opts the user INTO being tracked, so a super
    // admin is tracked only when a role/override actually grants it.
    canBeProductivityTracked: hasPermission(PERMISSIONS.CSR_PRODUCTIVITY_TRACKING),
    canManageCsrProductivity: isSuperAdmin || hasPermission(PERMISSIONS.CSR_PRODUCTIVITY_MANAGE),
    canManageCsrClockSessions: isSuperAdmin || hasPermission(PERMISSIONS.CSR_CLOCK_MANAGE),
    // Deliberately no isSuperAdmin shortcut (and super admins excluded outright,
    // since they implicitly receive every permission): this LOCKS the app until
    // the user clocks in, so only an explicit grant to a regular user counts.
    mustClockIn: !isSuperAdmin && userPermissions.includes(PERMISSIONS.CLOCKIN_REQUIRED),
  canViewEmployeeClock: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_CLOCK_VIEW),
  canManageEmployeeClock: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_CLOCK_MANAGE),
    canCsrSmsLeaderboardView: isSuperAdmin || hasPermission(PERMISSIONS.TECH_DASHBOARD_CSR_SMS_LEADERBOARD_VIEW),
    canManageServices: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_SERVICES),

    
    // Customer
    canCustomerView: isSuperAdmin || hasPermission(PERMISSIONS.CUSTOMER_VIEW),
    canCustomerCreate: isSuperAdmin || hasPermission(PERMISSIONS.CUSTOMER_CREATE),
    canCustomerUpdate: isSuperAdmin || hasPermission(PERMISSIONS.CUSTOMER_UPDATE),
    canCustomerAddressUpdate: isSuperAdmin || hasPermission(PERMISSIONS.CUSTOMER_ADDRESS_UPDATE),
    canCustomerSmsPreferencesUpdate: isSuperAdmin || hasPermission(PERMISSIONS.CUSTOMER_SMS_PREFERENCES_UPDATE),
    canCustomerDelete: isSuperAdmin || hasPermission(PERMISSIONS.CUSTOMER_DELETE),
    canCustomerCreationReportView: isSuperAdmin || hasPermission(PERMISSIONS.CUSTOMER_CREATION_REPORT_VIEW),
    canCustomerImport: isSuperAdmin || hasPermission(PERMISSIONS.CUSTOMER_IMPORT),
    canCustomerImportRollback: isSuperAdmin || hasPermission(PERMISSIONS.CUSTOMER_IMPORT_ROLLBACK),
    canManageDispatchesAndZoneMap: isSuperAdmin || hasPermission(PERMISSIONS.JOB_MANAGE) || hasPermission(PERMISSIONS.ZONE_MAP_MANAGE) || hasPermission(PERMISSIONS.RESCHEDULE_REQUEST_REVIEW),
    
    canManageDispatches: isSuperAdmin || hasPermission(PERMISSIONS.JOB_MANAGE),
    canManageZoneMap: isSuperAdmin || hasPermission(PERMISSIONS.ZONE_MAP_MANAGE),
    // Soft-deleting a job's appointment (board unassign / drag to Unscheduled /
    // remove last technician / release orphaned slot). Mirrors the JobsApi gate.
    canRemoveAppointment: isSuperAdmin || hasPermission(PERMISSIONS.APPOINTMENT_REMOVE),
    canReviewRescheduleRequests: isSuperAdmin || hasPermission(PERMISSIONS.RESCHEDULE_REQUEST_REVIEW),
    // Archive a job from the EDB detail panel / Job List, or bring one back. Mirrors the JobsApi gate.
    canArchiveDispatches: isSuperAdmin || hasPermission(PERMISSIONS.DISPATCH_ARCHIVE),
    // Edit the description (job notes) of a direct job from the EDB detail panel. Mirrors the JobsApi gate.
    canEditDispatchDescription: isSuperAdmin || hasPermission(PERMISSIONS.DISPATCH_DESCRIPTION_UPDATE),
    canReviewJobReports: isSuperAdmin || hasPermission(PERMISSIONS.JOB_REPORT_REVIEW),
    // Inventory pillar + Parts List page
    canAccessInventory: isSuperAdmin || hasAnyPermission([PERMISSIONS.INVENTORY_PARTS_VIEW, PERMISSIONS.INVENTORY_PARTS_MANAGE]),
    canViewPartsList: isSuperAdmin || hasAnyPermission([PERMISSIONS.INVENTORY_PARTS_VIEW, PERMISSIONS.INVENTORY_PARTS_MANAGE]),
    canManagePartsList: isSuperAdmin || hasPermission(PERMISSIONS.INVENTORY_PARTS_MANAGE),
    canDeleteJobReports: isSuperAdmin || hasPermission(PERMISSIONS.JOB_REPORT_DELETE),
    canViewReports: isSuperAdmin || hasPermission(PERMISSIONS.VIEW_REPORTS),
    canManageSettings: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_SETTINGS),
    canManageVehicles: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_VEHICLES),
    canManageInventory: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_INVENTORY),
    canManagePayments: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_PAYMENTS),
    canManageContracts: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_CONTRACTS),
    canManageDocuments: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_DOCUMENTS),
    canManageNotifications: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_NOTIFICATIONS),
    canManageIntegrations: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_INTEGRATIONS),
    canManageApiKeys: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_API_KEYS),
    canManageWebhooks: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_WEBHOOKS),
    canViewChat: isSuperAdmin || hasPermission(PERMISSIONS.CHAT_VIEW),
    canManageChatChannels: isSuperAdmin || hasPermission(PERMISSIONS.CHAT_CHANNELS_MANAGE),
    canManageChatUserSync: isSuperAdmin || hasPermission(PERMISSIONS.CHAT_USER_SYNC_MANAGE),

    // Marketing (SMS Campaign service)
    canAccessMarketing: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.MARKETING_DASHBOARD_VIEW,
      PERMISSIONS.MARKETING_CONTACTS_MANAGE,
      PERMISSIONS.MARKETING_LISTS_MANAGE,
      PERMISSIONS.MARKETING_CAMPAIGNS_MANAGE,
      PERMISSIONS.MARKETING_TEMPLATES_MANAGE,
      PERMISSIONS.MARKETING_INBOX_MANAGE,
      PERMISSIONS.MARKETING_SUPPRESSIONS_MANAGE,
      PERMISSIONS.MARKETING_SETTINGS_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_ACCOUNTS_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_PLANNER_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_PLANNER_SUBMIT,
      PERMISSIONS.MARKETING_SOCIAL_PLANNER_APPROVE,
      PERMISSIONS.MARKETING_SOCIAL_CONTENT_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_LEADS_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_ADS_VIEW,
      PERMISSIONS.MARKETING_SOCIAL_ADS_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_ADS_CREATE,
      PERMISSIONS.MARKETING_SOCIAL_REVIEW_TRACKING_VIEW,
      PERMISSIONS.MARKETING_SOCIAL_REVIEW_TRACKING_MANAGE,
    ]),
    // Marketing menu groups
    canAccessMarketingSms: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.MARKETING_DASHBOARD_VIEW,
      PERMISSIONS.MARKETING_CONTACTS_MANAGE,
      PERMISSIONS.MARKETING_LISTS_MANAGE,
      PERMISSIONS.MARKETING_CAMPAIGNS_MANAGE,
      PERMISSIONS.MARKETING_TEMPLATES_MANAGE,
      PERMISSIONS.MARKETING_INBOX_MANAGE,
      PERMISSIONS.MARKETING_SUPPRESSIONS_MANAGE,
      PERMISSIONS.MARKETING_SETTINGS_MANAGE,
    ]),
    // Google Business menu: the Social permissions plus Review Tracking
    canAccessMarketingSocialNetwork: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.MARKETING_SOCIAL_ACCOUNTS_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_CONTENT_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_REVIEW_TRACKING_VIEW,
      PERMISSIONS.MARKETING_SOCIAL_REVIEW_TRACKING_MANAGE,
    ]),
    // Facebook menu also holds Leads and Ads
    canAccessMarketingFacebook: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.MARKETING_SOCIAL_ACCOUNTS_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_CONTENT_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_LEADS_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_ADS_VIEW,
      PERMISSIONS.MARKETING_SOCIAL_ADS_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_ADS_CREATE,
    ]),
    canMarketingDashboardView: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_DASHBOARD_VIEW),
    canMarketingContactsManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_CONTACTS_MANAGE),
    canMarketingListsManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_LISTS_MANAGE),
    canMarketingCampaignsManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_CAMPAIGNS_MANAGE),
    canMarketingTemplatesManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_TEMPLATES_MANAGE),
    canMarketingInboxManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_INBOX_MANAGE),
    canMarketingSuppressionsManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SUPPRESSIONS_MANAGE),
    canMarketingSettingsManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SETTINGS_MANAGE),
    canMarketingSocialAccountsManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SOCIAL_ACCOUNTS_MANAGE),
    // Social Planner page: manage (publish directly), submit (request approval) or approve (review)
    canAccessMarketingSocialPlanner: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.MARKETING_SOCIAL_PLANNER_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_PLANNER_SUBMIT,
      PERMISSIONS.MARKETING_SOCIAL_PLANNER_APPROVE,
    ]),
    canMarketingSocialPlannerManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SOCIAL_PLANNER_MANAGE),
    canMarketingSocialPlannerSubmit: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SOCIAL_PLANNER_SUBMIT),
    canMarketingSocialPlannerApprove: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SOCIAL_PLANNER_APPROVE),
    canMarketingSocialContentManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SOCIAL_CONTENT_MANAGE),
    canMarketingSocialLeadsManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SOCIAL_LEADS_MANAGE),
    // Marketing > Google Business > Review Tracking; manage = change credits, badge links, sync now
    canViewGoogleReviewTracking: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.MARKETING_SOCIAL_REVIEW_TRACKING_VIEW,
      PERMISSIONS.MARKETING_SOCIAL_REVIEW_TRACKING_MANAGE,
    ]),
    canManageGoogleReviewTracking: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SOCIAL_REVIEW_TRACKING_MANAGE),
    // Dispatch Details > Google Review tab: text the review request (manage includes it)
    canSendGoogleReviewRequest: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.MARKETING_SOCIAL_REVIEW_TRACKING_SEND_REQUEST,
      PERMISSIONS.MARKETING_SOCIAL_REVIEW_TRACKING_MANAGE,
    ]),
    // Manage and Create include viewing the report
    canMarketingSocialAdsView: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.MARKETING_SOCIAL_ADS_VIEW,
      PERMISSIONS.MARKETING_SOCIAL_ADS_MANAGE,
      PERMISSIONS.MARKETING_SOCIAL_ADS_CREATE,
    ]),
    canMarketingSocialAdsManage: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SOCIAL_ADS_MANAGE),
    canMarketingSocialAdsCreate: isSuperAdmin || hasPermission(PERMISSIONS.MARKETING_SOCIAL_ADS_CREATE),
    canManageAuditLogs: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_AUDIT_LOGS),
    canManageBackups: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_BACKUPS),
    canManageSystemHealth: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_SYSTEM_HEALTH),

    // Role management access (super admin or requires either tenant or employee role management)
    canAccessRoleManagement: isSuperAdmin || hasAnyPermission([PERMISSIONS.MANAGE_TENANT_ROLES, PERMISSIONS.MANAGE_EMPLOYEE_ROLES]),

    // Permission management access (super admin or requires either tenant or employee role management)
    canAccessPermissionManagement: isSuperAdmin || hasAnyPermission([PERMISSIONS.MANAGE_TENANT_ROLES, PERMISSIONS.MANAGE_EMPLOYEE_ROLES]),

    // GPS
    canAccessGps: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.GPS_JOB_BOARD_VIEW,
      PERMISSIONS.GPS_JOB_NOTES_VIEW,
      PERMISSIONS.GPS_JOB_STATUS_VIEW,
      PERMISSIONS.GPS_DRIVER_DEVICE_VIEW,
      PERMISSIONS.GPS_DRIVER_DEVICE_DAILY_SUMMARY_VIEW,
      PERMISSIONS.GPS_DRIVER_DEVICE_ROUTE_VIEW,
      PERMISSIONS.GPS_DRIVER_DEVICE_ALERT_VIEW,
      PERMISSIONS.GPS_DRIVER_DEVICE_DTC_VIEW,
      PERMISSIONS.GPS_DRIVER_DEVICE_OIL_CHANGE_VIEW,
      PERMISSIONS.GPS_DRIVER_DEVICE_GROUP_VIEW,
    ]),
    canAccessGpsJobBoard: isSuperAdmin || hasPermission(PERMISSIONS.GPS_JOB_BOARD_VIEW),
    canAccessGpsJobNotes: isSuperAdmin || hasPermission(PERMISSIONS.GPS_JOB_NOTES_VIEW),
    canGpsJobNotesCreate: isSuperAdmin || hasPermission(PERMISSIONS.GPS_JOB_NOTES_CREATE),
    canGpsJobNotesUpdate: isSuperAdmin || hasPermission(PERMISSIONS.GPS_JOB_NOTES_UPDATE),
    canGpsJobNotesDelete: isSuperAdmin || hasPermission(PERMISSIONS.GPS_JOB_NOTES_DELETE),
    canAccessGpsJobStatus: isSuperAdmin || hasPermission(PERMISSIONS.GPS_JOB_STATUS_VIEW),
    canGpsJobStatusCreate: isSuperAdmin || hasPermission(PERMISSIONS.GPS_JOB_STATUS_CREATE),
    canGpsJobStatusUpdate: isSuperAdmin || hasPermission(PERMISSIONS.GPS_JOB_STATUS_UPDATE),
    canGpsJobStatusDelete: isSuperAdmin || hasPermission(PERMISSIONS.GPS_JOB_STATUS_DELETE),
    canAccessGpsDriverDevices: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_VIEW),
    canDriverDeviceDownSync: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_DOWN_SYNC),
    canDriverDeviceUpSync: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_UP_SYNC),
    canAccessGpsDriverDeviceDailySummary: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_DAILY_SUMMARY_VIEW),
    canCalculateGpsDriverDeviceDailySummary: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_DAILY_SUMMARY_CALCULATE),
    canAccessGpsDriverDeviceRoute: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_ROUTE_VIEW),
    canAccessGpsDriverDeviceDriveStop: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_DRIVE_STOP_VIEW),
    canDriverDeviceDriveStopDownSync: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_DRIVE_STOP_DOWN_SYNC),
    canAccessGpsDriverDeviceAlert: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_ALERT_VIEW),
    canDriverDeviceAlertDownSync: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_ALERT_DOWN_SYNC),
    canAccessGpsOilChangeNotification: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_OIL_CHANGE_VIEW),
    canCompleteGpsOilChangeNotification: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_OIL_CHANGE_COMPLETE),
    canAccessGpsDriverDeviceDtc: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_DTC_VIEW),
    canDriverDeviceDtcDownSync: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_DTC_DOWN_SYNC),
    canAccessGpsDriverDeviceGroup: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_GROUP_VIEW),
    canDriverDeviceGroupDownSync: isSuperAdmin || hasPermission(PERMISSIONS.GPS_DRIVER_DEVICE_GROUP_DOWN_SYNC),
    canAccessGpsDriverDeviceReport: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.GPS_DRIVER_DEVICE_ALERT_VIEW,
      PERMISSIONS.GPS_DRIVER_DEVICE_DTC_VIEW,
    ]),

    // Form
    canAccessForms: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.FORM_VIEW,
      PERMISSIONS.FORM_CREATE,
      PERMISSIONS.FORM_RESPONSE_VIEW,
      PERMISSIONS.FORM_TRIGGER_VIEW,
    ]),
    canFormView: isSuperAdmin || hasPermission(PERMISSIONS.FORM_VIEW),
    canFormCreate: isSuperAdmin || hasPermission(PERMISSIONS.FORM_CREATE),
    canFormUpdate: isSuperAdmin || hasPermission(PERMISSIONS.FORM_UPDATE),
    canFormDelete: isSuperAdmin || hasPermission(PERMISSIONS.FORM_DELETE),
    canFormExport: isSuperAdmin || hasPermission(PERMISSIONS.FORM_EXPORT),
    canFormImport: isSuperAdmin || hasPermission(PERMISSIONS.FORM_IMPORT),
    canFormFieldView: isSuperAdmin || hasPermission(PERMISSIONS.FORM_FIELD_VIEW),
    canFormFieldCreate: isSuperAdmin || hasPermission(PERMISSIONS.FORM_FIELD_CREATE),
    canFormFieldUpdate: isSuperAdmin || hasPermission(PERMISSIONS.FORM_FIELD_UPDATE),
    canFormFieldDelete: isSuperAdmin || hasPermission(PERMISSIONS.FORM_FIELD_DELETE),
    canFormResponseView: isSuperAdmin || hasPermission(PERMISSIONS.FORM_RESPONSE_VIEW),
    canFormResponseDelete: isSuperAdmin || hasPermission(PERMISSIONS.FORM_RESPONSE_DELETE),
    canFormResponseExport: isSuperAdmin || hasPermission(PERMISSIONS.FORM_RESPONSE_EXPORT),
    canFormResponseSubmit: isSuperAdmin || hasPermission(PERMISSIONS.FORM_RESPONSE_SUBMIT),
    canFormTriggerView: isSuperAdmin || hasPermission(PERMISSIONS.FORM_TRIGGER_VIEW),
    canFormTriggerCreate: isSuperAdmin || hasPermission(PERMISSIONS.FORM_TRIGGER_CREATE),
    canFormTriggerUpdate: isSuperAdmin || hasPermission(PERMISSIONS.FORM_TRIGGER_UPDATE),
    canFormTriggerDelete: isSuperAdmin || hasPermission(PERMISSIONS.FORM_TRIGGER_DELETE),
    canFormTriggerExecute: isSuperAdmin || hasPermission(PERMISSIONS.FORM_TRIGGER_EXECUTE),

    // Invoice
    canAccessInvoice: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.INVOICE_VIEW,
      PERMISSIONS.INVOICE_CREATE,
    ]),
    canInvoiceView: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_VIEW),
    canInvoiceCreate: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_CREATE),
    canInvoiceUpdate: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_UPDATE),
    canInvoiceDelete: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_DELETE),
    canInvoiceUpdateStatus: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_UPDATE_STATUS),
    // canInvoiceEsignStart: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_ESIGN_START),
    // canInvoiceEsignStatus: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_ESIGN_STATUS),
    canInvoiceSignnowStart: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_SIGNNOW_START),
    // canInvoiceSignnowStatus: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_SIGNNOW_STATUS),
    // canInvoiceSignnowSessions: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_SIGNNOW_SESSIONS),
    canInvoiceSignnowSync: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_SIGNNOW_SYNC),

    // SignNow template documents
    canManageSignNowTemplates: isSuperAdmin || hasPermission(PERMISSIONS.SIGNNOW_TEMPLATES_MANAGE),
    canSendSignNowDocuments: isSuperAdmin || hasPermission(PERMISSIONS.SIGNNOW_DOCUMENTS_SEND),
    canInvoiceTopCustomers: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_TOP_CUSTOMERS),
    canManagePaymentGateways: isSuperAdmin || hasPermission(PERMISSIONS.INVOICE_PAYMENT_GATEWAYS_MANAGE),

    // Coupon
    canAccessCoupon: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.COUPON_VIEW,
      PERMISSIONS.COUPON_CREATE,
    ]),
    canCouponView: isSuperAdmin || hasPermission(PERMISSIONS.COUPON_VIEW),
    canCouponCreate: isSuperAdmin || hasPermission(PERMISSIONS.COUPON_CREATE),
    canCouponUpdate: isSuperAdmin || hasPermission(PERMISSIONS.COUPON_UPDATE),
    canCouponDelete: isSuperAdmin || hasPermission(PERMISSIONS.COUPON_DELETE),

    // Sales Follow-ups
    canSalesView: isSuperAdmin || hasPermission(PERMISSIONS.SALES_VIEW),
    canSalesUpdate: isSuperAdmin || hasPermission(PERMISSIONS.SALES_UPDATE),

    // Estimate
    canAccessEstimate: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.ESTIMATE_VIEW,
      PERMISSIONS.ESTIMATE_CREATE,
    ]),
    canEstimateView: isSuperAdmin || hasPermission(PERMISSIONS.ESTIMATE_VIEW),
    canEstimateCreate: isSuperAdmin || hasPermission(PERMISSIONS.ESTIMATE_CREATE),
    canEstimateUpdate: isSuperAdmin || hasPermission(PERMISSIONS.ESTIMATE_UPDATE),
    canEstimateDelete: isSuperAdmin || hasPermission(PERMISSIONS.ESTIMATE_DELETE),
    canEstimateExportPdf: isSuperAdmin || hasPermission(PERMISSIONS.ESTIMATE_EXPORT_PDF),

    canAccessEstimatesOrInvoices: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.ESTIMATE_VIEW,
      PERMISSIONS.ESTIMATE_CREATE,
      PERMISSIONS.INVOICE_VIEW,
      PERMISSIONS.INVOICE_CREATE,
    ]),

    // Pricebook
    canAccessPricebook: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.PRICEBOOK_VIEW,
      PERMISSIONS.PRICEBOOK_CREATE,
      PERMISSIONS.PRICEBOOK_CATEGORY_VIEW,
      PERMISSIONS.PRICEBOOK_CATEGORY_CREATE,
    ]),
    canPricebookView: isSuperAdmin || hasPermission(PERMISSIONS.PRICEBOOK_VIEW),
    canPricebookCreate: isSuperAdmin || hasPermission(PERMISSIONS.PRICEBOOK_CREATE),
    canPricebookUpdate: isSuperAdmin || hasPermission(PERMISSIONS.PRICEBOOK_UPDATE),
    canPricebookDelete: isSuperAdmin || hasPermission(PERMISSIONS.PRICEBOOK_DELETE),
    canPricebookImport: isSuperAdmin || hasPermission(PERMISSIONS.PRICEBOOK_IMPORT),
    // Service benefit suggestions in the Proposal editor — readable by estimate editors too (mirrors URL_2_PERM).
    canPricebookServiceBenefits: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.PRICEBOOK_VIEW,
      PERMISSIONS.ESTIMATE_CREATE,
      PERMISSIONS.ESTIMATE_UPDATE,
    ]),

    canPricebookCategoryView: isSuperAdmin || hasPermission(PERMISSIONS.PRICEBOOK_CATEGORY_VIEW),
    canPricebookCategoryCreate: isSuperAdmin || hasPermission(PERMISSIONS.PRICEBOOK_CATEGORY_CREATE),
    canPricebookCategoryUpdate: isSuperAdmin || hasPermission(PERMISSIONS.PRICEBOOK_CATEGORY_UPDATE),
    canPricebookCategoryDelete: isSuperAdmin || hasPermission(PERMISSIONS.PRICEBOOK_CATEGORY_DELETE),

    // Memberships
    canMembershipView: isSuperAdmin || hasPermission(PERMISSIONS.MEMBERSHIP_VIEW),
    canMembershipCreate: isSuperAdmin || hasPermission(PERMISSIONS.MEMBERSHIP_CREATE),
    canMembershipUpdate: isSuperAdmin || hasPermission(PERMISSIONS.MEMBERSHIP_UPDATE),
    canMembershipManage: isSuperAdmin || hasPermission(PERMISSIONS.MEMBERSHIP_MANAGE),

    // call board
    canCallBoardMenu: isSuperAdmin || hasAnyPermission([PERMISSIONS.CALL_BOARD_MANAGE, PERMISSIONS.CALL_BOARD_VIEW,]),
    // Managers see every CSR's private (wrap-up) callbacks — Call Board "Team" tab.
    canViewTeamCallbacks: isSuperAdmin || hasPermission(PERMISSIONS.CALL_BOARD_MANAGE),

    // sms board
    canSmsBoardMenu: isSuperAdmin || hasAnyPermission([PERMISSIONS.SMS_BOARD_MANAGE, PERMISSIONS.SMS_BOARD_VIEW]),
    canSmsBoardTextAllTechs: isSuperAdmin || hasPermission(PERMISSIONS.SMS_BOARD_TEXT_ALL_TECHS),

    // Gmail Inbox
    canAccessGmailInbox: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.GMAIL_INBOX_VIEW,
      PERMISSIONS.GMAIL_INBOX_SEND,
      PERMISSIONS.GMAIL_INBOX_MANAGE,
    ]),
    canGmailInboxView: isSuperAdmin || hasPermission(PERMISSIONS.GMAIL_INBOX_VIEW),
    canGmailInboxSend: isSuperAdmin || hasPermission(PERMISSIONS.GMAIL_INBOX_SEND),
    canGmailInboxManage: isSuperAdmin || hasPermission(PERMISSIONS.GMAIL_INBOX_MANAGE),

    // Email
    canEmailTemplateView: isSuperAdmin || hasPermission(PERMISSIONS.EMAIL_TEMPLATE_VIEW),
    canEmailTemplateCreate: isSuperAdmin || hasPermission(PERMISSIONS.EMAIL_TEMPLATE_CREATE),
    canEmailTemplateUpdate: isSuperAdmin || hasPermission(PERMISSIONS.EMAIL_TEMPLATE_UPDATE),
    canEmailTemplateDelete: isSuperAdmin || hasPermission(PERMISSIONS.EMAIL_TEMPLATE_DELETE),
    canEmailTemplateCategoryManage: isSuperAdmin || hasPermission(PERMISSIONS.EMAIL_TEMPLATE_CATEGORY_MANAGE),
    canEmailSignatureManage: isSuperAdmin || hasPermission(PERMISSIONS.EMAIL_SIGNATURE_MANAGE),
    canAccessEmailTemplates: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.EMAIL_TEMPLATE_VIEW,
      PERMISSIONS.EMAIL_TEMPLATE_CATEGORY_MANAGE,
      PERMISSIONS.EMAIL_SIGNATURE_MANAGE,
    ]),

    // CSR Numbers (shared-inbox number assignment — TenantInfoPage / Call Board)
    canCsrSupportManageNumbers: isSuperAdmin || hasPermission(PERMISSIONS.CSR_SUPPORT_MANAGE_NUMBERS),

    // CSR Availability Control
    // Has the permission at all (required to toggle any availability)
    canToggleCallAvailability: isSuperAdmin || hasPermission(PERMISSIONS.CSR_AVAILABILITY_CONTROL),
    // Has the permission AND is not a CSR — can toggle anyone's availability
    canToggleAnyCallAvailability: (isSuperAdmin || hasPermission(PERMISSIONS.CSR_AVAILABILITY_CONTROL)) && !isCurrentUserCsr,

    // Call Wrap-up — Escalations tab of the Call Follow-ups page
    canHandleCallEscalations: isSuperAdmin || hasPermission(PERMISSIONS.CALL_WRAPUP_ESCALATION_HANDLE),
    canViewCallFollowups: isSuperAdmin || hasPermission(PERMISSIONS.CALL_WRAPUP_ESCALATION_HANDLE) || hasPermission(PERMISSIONS.ALL_CALLS_VIEW),
    // Agent Status — CSR Status page (view every CSR's state, force status changes, history)
    canManageAgentStatus: isSuperAdmin || hasPermission(PERMISSIONS.AGENT_STATUS_MANAGE),
    // Call Handoff Monitor — appointments.center → CSR accept/bridge diagnostics
    canViewCallHandoffMonitor: isSuperAdmin || hasPermission(PERMISSIONS.CALL_HANDOFF_MONITOR_VIEW),

    // Technician Route Locking
    canManageRouteLocks: isSuperAdmin || hasPermission(PERMISSIONS.DISPATCH_ROUTE_LOCK_MANAGE),

    // Settings
    canAccessSettings: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.MANAGE_SETTINGS,
      PERMISSIONS.MANAGE_MESSAGE_TEMPLATES,
      PERMISSIONS.MANAGE_TENANT_ADDRESSES,
      PERMISSIONS.MANAGE_TENANT_INFO,
      PERMISSIONS.MANAGE_TEXT_NOTE_TEMPLATE,
      PERMISSIONS.MANAGE_TAXONOMY,
      PERMISSIONS.SMS_TEMPLATE_VIEW,
      PERMISSIONS.SMS_TEMPLATE_CREATE,
      PERMISSIONS.SMS_TEMPLATE_TRIGGER_VIEW,
    ]),
    canManageMessageTemplates: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_MESSAGE_TEMPLATES),
    canManageTenantAddresses: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_TENANT_ADDRESSES),
    canManageTenantInfo: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_TENANT_INFO),
    canManageTextNoteTemplate: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_TEXT_NOTE_TEMPLATE),
    canManageTaxonomy: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_TAXONOMY),
    canManageTechnicianAvailability: isSuperAdmin || hasPermission(PERMISSIONS.MANAGE_TECHNICIAN_AVAILABILITY),

    // SMS Templates
    canAccessSmsTemplates: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.SMS_TEMPLATE_VIEW,
      PERMISSIONS.SMS_TEMPLATE_CREATE,
      PERMISSIONS.SMS_TEMPLATE_UPDATE,
      PERMISSIONS.SMS_TEMPLATE_DELETE,
      PERMISSIONS.SMS_TEMPLATE_TRIGGER_VIEW,
      PERMISSIONS.SMS_TEMPLATE_TRIGGER_CREATE,
      PERMISSIONS.SMS_TEMPLATE_TRIGGER_UPDATE,
      PERMISSIONS.SMS_TEMPLATE_TRIGGER_DELETE,
    ]),
    canSmsTemplateView: isSuperAdmin || hasPermission(PERMISSIONS.SMS_TEMPLATE_VIEW),
    canSmsTemplateCreate: isSuperAdmin || hasPermission(PERMISSIONS.SMS_TEMPLATE_CREATE),
    canSmsTemplateUpdate: isSuperAdmin || hasPermission(PERMISSIONS.SMS_TEMPLATE_UPDATE),
    canSmsTemplateDelete: isSuperAdmin || hasPermission(PERMISSIONS.SMS_TEMPLATE_DELETE),
    canSmsTemplateTriggerView: isSuperAdmin || hasPermission(PERMISSIONS.SMS_TEMPLATE_TRIGGER_VIEW),
    canSmsTemplateTriggerCreate: isSuperAdmin || hasPermission(PERMISSIONS.SMS_TEMPLATE_TRIGGER_CREATE),
    canSmsTemplateTriggerUpdate: isSuperAdmin || hasPermission(PERMISSIONS.SMS_TEMPLATE_TRIGGER_UPDATE),
    canSmsTemplateTriggerDelete: isSuperAdmin || hasPermission(PERMISSIONS.SMS_TEMPLATE_TRIGGER_DELETE),

    // Monitor
    canAccessMonitor: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.VIEW_DISPATCH_GREETING_LOGS,
      PERMISSIONS.VIEW_SIGNNOW_WEBHOOK_MONITOR,
      PERMISSIONS.VIEW_LIVE_CALLS_MONITOR,
      PERMISSIONS.VIEW_FRONTDOOR_MONITOR,
      PERMISSIONS.VIEW_SURVEY_FOLLOWUP_MONITOR,
      PERMISSIONS.VIEW_NCC_FOLLOWUP_MONITOR,
      PERMISSIONS.VIEW_FIFTEEN_PLUS_MONITOR,
      PERMISSIONS.HVAC_AGE_JOURNEY_VIEW,
      PERMISSIONS.ALL_CALLS_VIEW,
      PERMISSIONS.ALL_SMS_VIEW,
      PERMISSIONS.JOB_RECORDINGS_VIEW,
      PERMISSIONS.JOB_RECORDING_UPLOADS_VIEW,
      PERMISSIONS.MONITOR_MOBILE_APP_LOGS_VIEW,
      PERMISSIONS.MONITOR_ESIGN_DOCUMENTS_VIEW,
      PERMISSIONS.VIEW_AUDIT_TRAIL,
      PERMISSIONS.VIEW_CUSTOMER_ROUTE_REMINDER_LOG,
    ]),
    canViewDispatchGreetingLogs: isSuperAdmin || hasPermission(PERMISSIONS.VIEW_DISPATCH_GREETING_LOGS),
    canViewSignnowWebhookMonitor: isSuperAdmin || hasPermission(PERMISSIONS.VIEW_SIGNNOW_WEBHOOK_MONITOR),
    canViewLiveCallsMonitor: isSuperAdmin || hasPermission(PERMISSIONS.VIEW_LIVE_CALLS_MONITOR),
    canViewFrontdoorMonitor: isSuperAdmin || hasPermission(PERMISSIONS.VIEW_FRONTDOOR_MONITOR),
    canViewSurveyFollowupMonitor: isSuperAdmin || hasPermission(PERMISSIONS.VIEW_SURVEY_FOLLOWUP_MONITOR),
    canViewNccFollowupMonitor: isSuperAdmin || hasPermission(PERMISSIONS.VIEW_NCC_FOLLOWUP_MONITOR),
    canViewFifteenPlusMonitor: isSuperAdmin || hasPermission(PERMISSIONS.VIEW_FIFTEEN_PLUS_MONITOR),
    canViewHvacAgeJourney: isSuperAdmin || hasPermission(PERMISSIONS.HVAC_AGE_JOURNEY_VIEW),
    canManageHvacAgeJourney: isSuperAdmin || hasPermission(PERMISSIONS.HVAC_AGE_JOURNEY_MANAGE),
    canLoginAsTechnician: isSuperAdmin || hasPermission(PERMISSIONS.MONITOR_LOGIN_AS_TECHNICIAN),
    canViewMobileAppLogs: isSuperAdmin || hasPermission(PERMISSIONS.MONITOR_MOBILE_APP_LOGS_VIEW),
    canViewESignDocuments: isSuperAdmin || hasPermission(PERMISSIONS.MONITOR_ESIGN_DOCUMENTS_VIEW),
    canViewAllCalls: isSuperAdmin || hasPermission(PERMISSIONS.ALL_CALLS_VIEW),
    canViewAllSms: isSuperAdmin || hasPermission(PERMISSIONS.ALL_SMS_VIEW),
    canViewJobRecordings: isSuperAdmin || hasPermission(PERMISSIONS.JOB_RECORDINGS_VIEW),
    canEditJobRecordings: isSuperAdmin || hasPermission(PERMISSIONS.JOB_RECORDINGS_EDIT),
    canViewJobRecordingUploads: isSuperAdmin || hasPermission(PERMISSIONS.JOB_RECORDING_UPLOADS_VIEW),
    canManageJobRecordingUploads: isSuperAdmin || hasPermission(PERMISSIONS.JOB_RECORDING_UPLOADS_MANAGE),
    canViewAuditTrail: isSuperAdmin || hasPermission(PERMISSIONS.VIEW_AUDIT_TRAIL),
    canViewCustomerRouteReminderLog: isSuperAdmin || hasPermission(PERMISSIONS.VIEW_CUSTOMER_ROUTE_REMINDER_LOG),
    canViewVehiclePolicySmsLog: isSuperAdmin || hasPermission(PERMISSIONS.VEHICLE_POLICY_SMS_LOG_VIEW),
    canViewAllNotifications: isSuperAdmin || hasPermission(PERMISSIONS.NOTIFICATION_VIEW_ALL),

    // Employee Text Notes
    canEmployeeTextNoteView: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_TEXT_NOTE_VIEW),
    canEmployeeTextNoteCreate: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_TEXT_NOTE_CREATE),
    canEmployeeTextNoteUpdate: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_TEXT_NOTE_UPDATE),
    canEmployeeTextNoteDelete: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_TEXT_NOTE_DELETE),

    // Employee Comments
    canEmployeeCommentView: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_COMMENT_VIEW),
    canEmployeeCommentCreate: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_COMMENT_CREATE),
    canEmployeeCommentUpdate: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_COMMENT_UPDATE),
    canEmployeeCommentDelete: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_COMMENT_DELETE),

    // Employee Attachment Sharing
    canEmployeeAttachmentShareView: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_ATTACHMENT_SHARE_VIEW),
    canEmployeeAttachmentShareCreate: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_ATTACHMENT_SHARE_CREATE),
    canEmployeeAttachmentShareDelete: isSuperAdmin || hasPermission(PERMISSIONS.EMPLOYEE_ATTACHMENT_SHARE_DELETE),

    // Time Log (Technician)
    canTimelogView: isSuperAdmin || hasPermission(PERMISSIONS.TIMELOG_VIEW),
    canTimelogCreate: isSuperAdmin || hasPermission(PERMISSIONS.TIMELOG_CREATE),
    canTimelogUpdate: isSuperAdmin || hasPermission(PERMISSIONS.TIMELOG_UPDATE),
    canTimelogDelete: isSuperAdmin || hasPermission(PERMISSIONS.TIMELOG_DELETE),

    // Jotform Integration
    canAccessJotform: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.JOTFORM_SETTINGS_VIEW,
      PERMISSIONS.JOTFORM_FORMS_VIEW,
      PERMISSIONS.JOTFORM_SUBMISSIONS_VIEW,
    ]),
    canJotformSettingsView: isSuperAdmin || hasPermission(PERMISSIONS.JOTFORM_SETTINGS_VIEW),
    canJotformSettingsUpdate: isSuperAdmin || hasPermission(PERMISSIONS.JOTFORM_SETTINGS_UPDATE),
    canJotformFormsView: isSuperAdmin || hasPermission(PERMISSIONS.JOTFORM_FORMS_VIEW),
    canJotformFormsSync: isSuperAdmin || hasPermission(PERMISSIONS.JOTFORM_FORMS_SYNC),
    canJotformQuestionsView: isSuperAdmin || hasPermission(PERMISSIONS.JOTFORM_QUESTIONS_VIEW),
    canJotformQuestionsSync: isSuperAdmin || hasPermission(PERMISSIONS.JOTFORM_QUESTIONS_SYNC),
    canJotformSubmissionsView: isSuperAdmin || hasPermission(PERMISSIONS.JOTFORM_SUBMISSIONS_VIEW),
    canJotformSubmissionsSync: isSuperAdmin || hasPermission(PERMISSIONS.JOTFORM_SUBMISSIONS_SYNC),
    canJotformSubmissionsLink: isSuperAdmin || hasPermission(PERMISSIONS.JOTFORM_SUBMISSIONS_LINK),

    // Dispatch Followup
    canAccessDispatchFollowups: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.DISPATCH_FOLLOWUP_VIEW,
      PERMISSIONS.DISPATCH_FOLLOWUP_LOGS_VIEW,
    ]),
    canDispatchFollowupView: isSuperAdmin || hasPermission(PERMISSIONS.DISPATCH_FOLLOWUP_VIEW),
    canDispatchFollowupLogsView: isSuperAdmin || hasPermission(PERMISSIONS.DISPATCH_FOLLOWUP_LOGS_VIEW),
    canDispatchFollowupTrigger: isSuperAdmin || hasPermission(PERMISSIONS.DISPATCH_FOLLOWUP_TRIGGER),

    // Appt Followup
    canAccessApptFollowups: isSuperAdmin || hasAnyPermission([
      PERMISSIONS.APPT_FOLLOWUP_VIEW,
      PERMISSIONS.APPT_FOLLOWUP_LOGS_VIEW,
    ]),
    canApptFollowupView: isSuperAdmin || hasPermission(PERMISSIONS.APPT_FOLLOWUP_VIEW),
    canApptFollowupLogsView: isSuperAdmin || hasPermission(PERMISSIONS.APPT_FOLLOWUP_LOGS_VIEW),
    canApptFollowupTrigger: isSuperAdmin || hasPermission(PERMISSIONS.APPT_FOLLOWUP_TRIGGER),

    // Technician Dashboard
    canAccessTechnicianDashboard: isSuperAdmin || hasPermission(PERMISSIONS.TECH_DASHBOARD_VIEW),
    canTechDashboardKpiEdit: isSuperAdmin || hasPermission(PERMISSIONS.TECH_DASHBOARD_KPI_EDIT),

    // Executive Dashboard
    canViewExecutiveDashboard: isSuperAdmin || hasPermission(PERMISSIONS.EXECUTIVE_DASHBOARD_VIEW),
    canManageExecutiveTargets: isSuperAdmin || hasPermission(PERMISSIONS.EXECUTIVE_TARGETS_MANAGE),

    // Automation Efficiency Dashboard
    canViewAutomationDashboard: isSuperAdmin || hasPermission(PERMISSIONS.AUTOMATION_DASHBOARD_VIEW),

    // User permissions array for direct access
    userPermissions,
  };

  return perm;
}
